/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { getUserFacingError, isCurrentPasswordIncorrect } from "../../utils/userFacingError";
import { enterpriseAPI, RTA, sessionInfo } from "../..";
import { Add } from "@mui/icons-material";
import { Title } from "../../components/Title";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import Collapse from "@mui/material/Collapse";
import localforage from "localforage";
import "./config.css";
import { createTurboUpload, validateField } from "../../utils/utils";
import { UPLOAD_BACKLOG_NOTICE } from "../../components/UploadToast";
import { BannerFeedback } from "../../components/BannerFeedback";
import { AxiosError } from "axios";
import Skeleton from "@mui/material/Skeleton";
import { InputTel } from "../../components/InputTel";
import { clearEnterpriseAuthState } from "../../auth/auth";
import { useUser } from "../../components/UserContext";

async function logout() {
  // Use the shared clear so REFRESH_TOKEN is wiped too — otherwise a stale
  // refresh token survives logout and the 401 interceptor could silently
  // mint a fresh JWT after the user "logged out".
  await clearEnterpriseAuthState();
  window.location.href = "/";
}

export function Configurations() {
  // Sidebar mini avatar reads `avatar` from UserContext. Wire profile avatar
  // saves into it so the sidebar updates the moment the photo changes, without
  // waiting for a full auth refresh.
  const { setAvatar } = useUser();
  const [typeBasicFeedback, setTypeBasicFeedback] = React.useState<
    "error" | "success" | "warning" | "info"
  >("error");
  const [typePasswordFeedback, setTypePasswordFeedback] = React.useState<
    "error" | "success" | "warning" | "info"
  >("info");
  const [openBasicFeedback, setBasicFeedback] = React.useState(false);
  const [openPasswordFeedback, setPasswordFeedback] = React.useState(false);
  const [basicMessage, setBasicMessage] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");
  const [editInfoBasic, setEditInfoBasic] = React.useState(false);
  const [editPassword, setEditPassword] = React.useState(false);
  const [showAvatar, setShowAvatar] = React.useState("");
  const avatarObjectURLRef = React.useRef<string | null>(null);
  const [collaborator, setCollaborator] = React.useState({
    name: "",
    email: "",
    phone: "",
    avatar: "",
  });
  const [phoneAreaCode, setPhoneAreaCode] = React.useState(0);
  const [phoneNumber, setPhoneNumber] = React.useState(0);
  const [sendInProgress, setSendInProgress] = React.useState(false);
  const [collaboratorId, setCollaboratorId] = React.useState("");
  const pendingBasicInfo = React.useRef<{ name: string; phone: string } | null>(null);
  const [avatarLoading, setAvatarLoading] = React.useState(false);
  const wrongPasswordAttempts = React.useRef(0);

  // Controlled form state (replaces document.getElementById reads)
  const [nameField, setNameField] = React.useState("");
  const [passwordCurrent, setPasswordCurrent] = React.useState("");
  const [passwordNew, setPasswordNew] = React.useState("");
  const [passwordConfirm, setPasswordConfirm] = React.useState("");
  const avatarInputRef = React.useRef<HTMLInputElement>(null);
  const avatarPreviewRef = React.useRef<HTMLImageElement>(null);

  const updateAvatarUrl = React.useCallback((url: string, isObjectUrl = false) => {
    const shouldPersistObjectUrl = isObjectUrl || url.startsWith("blob:");

    if (
      avatarObjectURLRef.current &&
      avatarObjectURLRef.current !== url &&
      avatarObjectURLRef.current.startsWith("blob:")
    ) {
      URL.revokeObjectURL(avatarObjectURLRef.current);
    }

    avatarObjectURLRef.current = shouldPersistObjectUrl ? url : null;
    setShowAvatar(url);
  }, []);

  React.useEffect(() => {
    return () => {
      if (avatarObjectURLRef.current && avatarObjectURLRef.current.startsWith("blob:")) {
        URL.revokeObjectURL(avatarObjectURLRef.current);
      }
    };
  }, []);

  function parseBRPhone(raw: string) {
    const digits = (raw || "").replace(/\D/g, "");
    if (digits.startsWith("55") && digits.length >= 4) {
      return {
        area: Number(digits.slice(2, 4)),
        number: Number(digits.slice(4)),
      };
    }
    if (digits.length >= 2) {
      return {
        area: Number(digits.slice(0, 2)),
        number: Number(digits.slice(2)),
      };
    }
    return { area: 0, number: 0 };
  }

  async function getUserInfos() {
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) return false;

    const headers: { [key: string]: string | undefined } = {
      "X-JWT": JWT,
    };

    try {
      const result = await enterpriseAPI.get("/get-collaborator", { headers: headers });
      if (result.status === 200) {
        const data = result.data;
        setCollaborator({
          name: data.name,
          email: data.email,
          phone: data.phone,
          avatar: data.avatar,
        });
        const parsedPhone = parseBRPhone(data.phone);
        setPhoneAreaCode(parsedPhone.area);
        setPhoneNumber(parsedPhone.number);

        setNameField(data.name);

        if (data.avatar) {
          updateAvatarUrl(data.avatar);
          setAvatarLoading(true);
          if (avatarPreviewRef.current) {
            avatarPreviewRef.current.style.display = "block";
          }
        }
      }
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  async function saveBasicInfos() {
    const name = nameField.normalize("NFC");
    const avatarInput = avatarInputRef.current;
    const formattedPhone = `+55${String(phoneAreaCode || "").replace(/\D/g, "")}${String(phoneNumber || "").replace(/\D/g, "")}`;

    const errName = validateField.fullName(name);
    const errPhone = validateField.number(formattedPhone);

    if (errName instanceof Error) {
      setBasicMessage(errName.message);
      setTypeBasicFeedback("error");
      setBasicFeedback(true);
      return;
    }

    if (errPhone instanceof Error) {
      setBasicMessage(errPhone.message);
      setTypeBasicFeedback("error");
      setBasicFeedback(true);
      return;
    }

    pendingBasicInfo.current = { name, phone: formattedPhone };
    setSendInProgress(true);

    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) {
      setSendInProgress(false);
      return false;
    }

    if (avatarInput?.files?.length) {
      setTypeBasicFeedback("info");
      setBasicMessage("Enviando avatar para compressão...");
      setBasicFeedback(true);
      const mediaId = crypto.randomUUID();

      try {
        const res = await enterpriseAPI.post(
          "/create-ticket-collaborator",
          {
            expectedMedias: 1,
            mediaId,
          },
          {
            headers: {
              "X-JWT": JWT,
            },
          },
        );

        if (res.status !== 200) {
          throw new Error("ticket creation failed");
        }

        const { promise } = createTurboUpload({
          ticketId: res.data.ticketId,
          file: avatarInput.files![0],
          mediaId: mediaId,
          mediaType: "image",
          section: "collaboratorAvatar",
          getToken: () => Promise.resolve(localforage.getItem("JWT") as Promise<string>),
        });

        const result = await promise;
        if (!result.ok) {
          if (result.backlog) {
            // Backlog cap: a friendly "na fila" notice, never a red error.
            setBasicMessage(UPLOAD_BACKLOG_NOTICE);
            setTypeBasicFeedback("info");
            setBasicFeedback(true);
            pendingBasicInfo.current = null;
            setSendInProgress(false);
            return false;
          }
          const bannerMessage = getUserFacingError(result, {
            context: "upload",
            fallback: "Não foi possível enviar sua foto. Confira o arquivo e tente novamente.",
          });
          setBasicMessage(bannerMessage);
          setTypeBasicFeedback("error");
          setBasicFeedback(true);
          pendingBasicInfo.current = null;
          setSendInProgress(false);
          return false;
        }

        setBasicMessage("Processando os dados, aguarde...");
        setTypeBasicFeedback("info");
        setBasicFeedback(true);
        return false;
      } catch (error) {
        setBasicMessage(
          getUserFacingError(error, {
            fallback: "Não foi possível atualizar seus dados. Tente novamente em instantes.",
          }),
        );
        setTypeBasicFeedback("error");
        setBasicFeedback(true);
        pendingBasicInfo.current = null;
        setSendInProgress(false);
        return false;
      } finally {
        setEditInfoBasic(false);
      }
    }

    await applyBasicInfos();
    return false;
  }

  async function applyBasicInfos(_manifestUrl?: string) {
    const currentName = (pendingBasicInfo.current?.name ?? nameField).normalize("NFC");
    const currentPhone =
      pendingBasicInfo.current?.phone ??
      `+55${String(phoneAreaCode || "").replace(/\D/g, "")}${String(phoneNumber || "").replace(/\D/g, "")}`;

    if (!sendInProgress) setSendInProgress(true);
    const errName = validateField.fullName(currentName);
    const errPhone = validateField.number(currentPhone);

    if (errName instanceof Error) {
      setBasicMessage("Nome inválido");
      setTypeBasicFeedback("error");
      setBasicFeedback(true);
      setSendInProgress(false);
      return false;
    }

    if (errPhone instanceof Error) {
      setBasicMessage("Telefone inválido");
      setTypeBasicFeedback("error");
      setBasicFeedback(true);
      setSendInProgress(false);
      return false;
    }

    let targetId = collaboratorId;
    if (!targetId) {
      const info = await sessionInfo();
      if (!info) {
        setSendInProgress(false);
        return false;
      }
      targetId = info.iss;
      setCollaboratorId(info.iss);
    }

    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) {
      setSendInProgress(false);
      return false;
    }

    try {
      const payload: Record<string, string> = {
        targetId: targetId,
        newName: currentName,
        newPhone: currentPhone,
      };

      if (_manifestUrl) {
        payload.avatarManifestUrl = _manifestUrl;
      }

      const result = await enterpriseAPI.patch("/update-collaborator", payload, {
        headers: { "X-JWT": JWT },
      });
      if (result.status === 200) {
        const currentAvatarUrl = avatarObjectURLRef.current ?? collaborator.avatar;
        if (avatarInputRef.current) avatarInputRef.current.value = "";

        setCollaborator((prev) => ({
          ...prev,
          name: currentName,
          phone: currentPhone,
          avatar: currentAvatarUrl,
        }));
        updateAvatarUrl(currentAvatarUrl, !!avatarObjectURLRef.current);
        // Push the new avatar into UserContext so the sidebar mini avatar
        // updates reactively. Prefer the canonical manifest URL when the photo
        // just changed (the sidebar resolves it); otherwise reflect the value
        // the profile is now showing.
        if (_manifestUrl) {
          setAvatar(_manifestUrl);
        } else if (currentAvatarUrl && !currentAvatarUrl.startsWith("blob:")) {
          setAvatar(currentAvatarUrl);
        }
        setPhoneAreaCode(parseBRPhone(currentPhone).area);
        setPhoneNumber(parseBRPhone(currentPhone).number);
        setBasicMessage("Informações básicas atualizadas com sucesso.");
        setTypeBasicFeedback("success");
        setBasicFeedback(true);
      }
    } catch (error) {
      const errorMessage = getUserFacingError(error, {
        fallback: "Não foi possível atualizar seus dados. Tente novamente em instantes.",
      });
      setTypeBasicFeedback("error");
      setBasicFeedback(true);

      setBasicMessage(errorMessage);
      return false;
    } finally {
      pendingBasicInfo.current = null;
      if (editInfoBasic) setEditInfoBasic(false);
      setSendInProgress(false);
    }
  }

  async function savePassword() {
    const current = passwordCurrent;
    const newPassword = passwordNew;
    const confirm = passwordConfirm;

    const errPassword = validateField.password(newPassword);
    if (errPassword instanceof Error) {
      setPasswordError(errPassword.message);
      setPasswordFeedback(true);
      setTypePasswordFeedback("error");
      return;
    }

    if (newPassword !== confirm) {
      setPasswordError("Senhas não conferem.");
      setPasswordFeedback(true);
      setTypePasswordFeedback("error");
      return;
    }

    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) return false;

    const headers: { [key: string]: string | undefined } = {
      "X-JWT": JWT,
    };

    const body = {
      currentPassword: current,
      newPassword: newPassword,
    };

    try {
      const result = await enterpriseAPI.patch("/update-collaborator-password", body, {
        headers: headers,
      });
      if (result.status === 200) {
        wrongPasswordAttempts.current = 0;
        setPasswordError("Senha atualizada com sucesso");
        setTypePasswordFeedback("success");
        setPasswordFeedback(true);
        setEditPassword(false);
        cleanPasswordFields();
      }
    } catch (error) {
      const e = error as AxiosError;
      let errorMessage = getUserFacingError(error, {
        fallback: "Não foi possível atualizar sua senha. Tente novamente em instantes.",
      });
      setTypePasswordFeedback("error");
      setPasswordFeedback(true);

      if (!e.response) {
        setPasswordError(errorMessage);
        return;
      }

      if (isCurrentPasswordIncorrect(error)) {
        wrongPasswordAttempts.current += 1;
        const remaining = Math.max(0, 3 - wrongPasswordAttempts.current);

        if (wrongPasswordAttempts.current >= 3) {
          setPasswordError("Senha atual incorreta. Você será deslogado por segurança.");
          setTypePasswordFeedback("error");
          setPasswordFeedback(true);
          setTimeout(() => logout(), 500);
          return;
        }

        setPasswordError(
          `Senha atual incorreta.${remaining ? ` Restam ${remaining} tentativas.` : ""}`,
        );
        setTypePasswordFeedback("error");
        setPasswordFeedback(true);
        return;
      }

      const messageTable: { [key: number]: string } = {
        400: "Confira se a nova senha atende aos requisitos e preencha a senha atual.",
      };

      errorMessage = messageTable[e.response.status] || errorMessage;
      setPasswordError(errorMessage);
      return false;
    }
  }

  function ChangingBasicInfos(nameOverride?: string) {
    if (sendInProgress) return;

    const sameName = (nameOverride ?? nameField) === collaborator.name;
    const sameEmail = true; // email field is disabled, always same
    const parsed = parseBRPhone(collaborator.phone);
    const samePhone = parsed.area === phoneAreaCode && parsed.number === phoneNumber;

    const currentAvatarUrl = avatarObjectURLRef.current ?? collaborator.avatar;
    const hasAvatarChange = currentAvatarUrl !== collaborator.avatar;

    if (sameName && sameEmail && samePhone && !hasAvatarChange) {
      setEditInfoBasic(false);
      return;
    }

    setEditInfoBasic(true);
  }

  function ChangingPassword(current: string, newPwd: string, confirmPwd: string) {
    if (current === "" || newPwd === "" || confirmPwd === "") {
      setEditPassword(false);
      return;
    }

    setEditPassword(true);
  }

  function cleanPasswordFields() {
    setPasswordCurrent("");
    setPasswordNew("");
    setPasswordConfirm("");
    setEditPassword(false);
  }

  function resetDefaultBasicInfos() {
    setNameField(collaborator.name);
    updateAvatarUrl(collaborator.avatar);
    if (avatarInputRef.current) avatarInputRef.current.value = "";

    if (collaborator.avatar === "") {
      if (avatarPreviewRef.current) avatarPreviewRef.current.removeAttribute("style");
      setAvatarLoading(false);
    } else {
      setAvatarLoading(true);
    }

    const parsedPhone = parseBRPhone(collaborator.phone);
    setPhoneAreaCode(parsedPhone.area);
    setPhoneNumber(parsedPhone.number);
    setEditInfoBasic(false);
    setBasicFeedback(false);
  }

  function previewAvatar() {
    const avatarInput = avatarInputRef.current;
    if (!avatarInput?.files?.length) return;

    setEditInfoBasic(true);
    setAvatarLoading(true);
    setBasicFeedback(false);
    const previewUrl = URL.createObjectURL(avatarInput.files[0]);
    updateAvatarUrl(previewUrl, true);
    if (avatarPreviewRef.current) {
      avatarPreviewRef.current.style.display = "block";
    }
  }

  class SocketEvent {
    static handleCollaboratorAvatarUpdate = (payload: any) => {
      if (!pendingBasicInfo.current) return;
      if (payload.status === "success") {
        applyBasicInfos(payload.manifest);
        return;
      }

      pendingBasicInfo.current = null;
      setSendInProgress(false);
      setBasicMessage("Falha ao processar avatar.");
      setTypeBasicFeedback("error");
      setBasicFeedback(true);
    };
  }

  React.useEffect(() => {
    RTA.connect();
    getUserInfos();
  }, []);

  React.useEffect(() => {
    let eventName = "";
    sessionInfo().then((info) => {
      if (!info) return;
      eventName = `enterpriseRTACollaboratorAvatarStart-${info.org_id}-${info.iss}`;
      RTA.on(eventName, SocketEvent.handleCollaboratorAvatarUpdate);
    });

    return () => {
      if (eventName) {
        RTA.off(eventName, SocketEvent.handleCollaboratorAvatarUpdate);
      }
    };
  }, []);

  return (
    <div id="configPageContent">
      <div id="parentManagers">
        <div id="manageBasicInfos" className="blockManager">
          <Title title="Informações Básicas" subtitle="Altere as informações base da sua conta." />
          <BannerFeedback
            type={typeBasicFeedback}
            open={openBasicFeedback}
            message={basicMessage}
            callback={() => setBasicFeedback(false)}
            sx={{ marginTop: "var(--spacing-2xl)" }}
          />
          <div id="basicInfosForm">
            <label id="avatarSpace" htmlFor="avatarCol">
              {avatarLoading && showAvatar && (
                <Skeleton
                  variant="circular"
                  width="100%"
                  height="100%"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "var(--border-radius-4xl)",
                    margin: "0px !important",
                  }}
                />
              )}
              <img
                ref={avatarPreviewRef}
                src={showAvatar}
                id="avatarPreview"
                alt="userAvatar"
                style={{ opacity: avatarLoading ? 0 : 1 }}
                onLoad={() => setTimeout(() => setAvatarLoading(false), 250)}
                onError={() => setAvatarLoading(false)}
              />
              <Add sx={{ fontSize: "var(--space-20)" }} />
              <span>Adicione um avatar</span>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                id="avatarCol"
                onChange={previewAvatar}
                disabled={sendInProgress}
              />
            </label>
            <div id="basicInfosFields">
              <div className="identifyBasicInfo">
                <span>Nome:</span>
                <input
                  onChange={(e) => {
                    setNameField(e.target.value);
                    ChangingBasicInfos(e.target.value);
                  }}
                  type="text"
                  id="basicInfosName"
                  placeholder="Nome"
                  value={nameField}
                  disabled={sendInProgress}
                />
              </div>
              <div className="identifyBasicInfo">
                <span>Email:</span>
                <input
                  type="text"
                  id="basicInfosEmail"
                  placeholder="Email"
                  value={collaborator.email}
                  disabled
                  readOnly
                />
              </div>
              <div className="identifyBasicInfo">
                <span>Celular/Telefone:</span>
                <InputTel
                  id="basicInfosPhone"
                  areaCode={phoneAreaCode}
                  number={phoneNumber}
                  disabled={sendInProgress}
                  onChange={(areaCode, number) => {
                    setPhoneAreaCode(areaCode);
                    setPhoneNumber(number);
                    ChangingBasicInfos();
                  }}
                />
              </div>
            </div>
          </div>
          <Collapse in={editInfoBasic}>
            <div id="basicInfoActions">
              <button id="btnSaveBasicInfos" onClick={saveBasicInfos} disabled={sendInProgress}>
                Salvar
              </button>
              <button
                id="btnResetBasicInfos"
                onClick={resetDefaultBasicInfos}
                disabled={sendInProgress}
              >
                Cancelar
              </button>
            </div>
          </Collapse>
        </div>
        <div id="managePassword" className="blockManager">
          <Title
            title="Manutenção de Senha"
            subtitle="Altere a senha da sua conta quando necessário."
          />
          <BannerFeedback
            type={typePasswordFeedback}
            open={openPasswordFeedback}
            message={passwordError}
            callback={() => setPasswordFeedback(false)}
            sx={{ marginTop: "var(--spacing-2xl)" }}
          />
          <div id="passwordForm">
            <input
              type="password"
              id="passwordCurrent"
              placeholder="Senha Atual"
              value={passwordCurrent}
              onChange={(e) => {
                setPasswordCurrent(e.target.value);
                ChangingPassword(e.target.value, passwordNew, passwordConfirm);
              }}
              disabled={sendInProgress}
            />
            <input
              type="password"
              id="passwordNew"
              placeholder="Nova Senha"
              value={passwordNew}
              onChange={(e) => {
                setPasswordNew(e.target.value);
                ChangingPassword(passwordCurrent, e.target.value, passwordConfirm);
              }}
              disabled={sendInProgress}
            />
            <input
              type="password"
              id="passwordConfirm"
              placeholder="Confirme a Nova Senha"
              value={passwordConfirm}
              onChange={(e) => {
                setPasswordConfirm(e.target.value);
                ChangingPassword(passwordCurrent, passwordNew, e.target.value);
              }}
              disabled={sendInProgress}
            />
          </div>
          <Collapse in={editPassword}>
            <div id="basicInfoActions">
              <button id="btnSaveBasicInfos" onClick={savePassword} disabled={sendInProgress}>
                Salvar
              </button>
              <button
                id="btnResetBasicInfos"
                onClick={cleanPasswordFields}
                disabled={sendInProgress}
              >
                Cancelar
              </button>
            </div>
          </Collapse>
        </div>
      </div>
      <button id="configLogout" onClick={logout}>
        Encerrar a sessão
        <LogoutOutlinedIcon sx={{ fontSize: "var(--space-8)" }} />
      </button>
    </div>
  );
}
